import { len, norm } from '../core/math'
import type { Intent, InputSource } from './types'
import { ClassicAimHelper, emptyIntent } from './types'

interface Stick {
  el: HTMLElement
  knob: HTMLElement
  pointerId: number | null
  cx: number
  cy: number
  dx: number
  dy: number
}

/**
 * Mobile source: dual virtual sticks — left moves, right aims + autofires
 * while held — plus a dedicated magic button. Large, thumb-reachable,
 * bottom corners, landscape-friendly.
 */
export class TouchSource implements InputSource {
  private left: Stick
  private right: Stick
  private magicQueued = false
  private altQueued = false
  private overrideQueued = false
  private pauseQueued = false
  private classic = false
  private classicHelper = new ClassicAimHelper()
  private lastAim = { x: 1, y: 0 }
  private layer: HTMLElement

  constructor() {
    this.layer = document.getElementById('touch-layer')!
    this.layer.innerHTML = `
      <div class="stick stick-left"><div class="stick-knob"></div><span class="stick-label">MOVE</span></div>
      <div class="stick stick-right"><div class="stick-knob"></div><span class="stick-label">AIM + FIRE</span></div>
      <button class="touch-magic" aria-label="use magic">◆<span class="stick-label">CHARGE</span></button>
      <button class="touch-alt" aria-label="heavy shot">⬢<span class="stick-label">HEAVY</span></button>
      <button class="touch-override" aria-label="override slow motion">◐<span class="stick-label">OVERRIDE</span></button>
      <button class="touch-pause" aria-label="pause">II</button>`
    this.left = this.makeStick('.stick-left')
    this.right = this.makeStick('.stick-right')
    this.layer.querySelector('.touch-magic')!.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      this.magicQueued = true
    })
    this.layer.querySelector('.touch-alt')!.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      this.altQueued = true
    })
    this.layer.querySelector('.touch-override')!.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      this.overrideQueued = true
    })
    this.layer.querySelector('.touch-pause')!.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      this.pauseQueued = true
    })
  }

  setVisible(on: boolean): void {
    this.layer.classList.toggle('hidden', !on)
  }

  private makeStick(sel: string): Stick {
    const el = this.layer.querySelector(sel) as HTMLElement
    const knob = el.querySelector('.stick-knob') as HTMLElement
    const stick: Stick = { el, knob, pointerId: null, cx: 0, cy: 0, dx: 0, dy: 0 }
    const RADIUS = 52
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      stick.pointerId = e.pointerId
      const rect = el.getBoundingClientRect()
      stick.cx = rect.left + rect.width / 2
      stick.cy = rect.top + rect.height / 2
      el.setPointerCapture(e.pointerId)
    })
    el.addEventListener('pointermove', (e) => {
      if (stick.pointerId !== e.pointerId) return
      let dx = e.clientX - stick.cx
      let dy = e.clientY - stick.cy
      const l = Math.hypot(dx, dy)
      if (l > RADIUS) {
        dx = (dx / l) * RADIUS
        dy = (dy / l) * RADIUS
      }
      stick.dx = dx / RADIUS
      stick.dy = dy / RADIUS
      knob.style.transform = `translate(${dx}px, ${dy}px)`
    })
    const release = (e: PointerEvent) => {
      if (stick.pointerId !== e.pointerId) return
      stick.pointerId = null
      stick.dx = 0
      stick.dy = 0
      knob.style.transform = 'translate(0,0)'
    }
    el.addEventListener('pointerup', release)
    el.addEventListener('pointercancel', release)
    return stick
  }

  poll(_px: number, _py: number): Intent {
    const intent = emptyIntent()
    intent.move = { x: this.left.dx, y: this.left.dy }
    if (len(intent.move) > 1) intent.move = norm(intent.move)

    const aiming = this.right.pointerId !== null && len({ x: this.right.dx, y: this.right.dy }) > 0.25
    if (aiming) {
      this.lastAim = norm({ x: this.right.dx, y: this.right.dy })
      intent.fire = true
    }
    intent.aim = { ...this.lastAim }
    intent.magic = this.magicQueued
    // interact doubles as the blaster's heavy shot (auto-pickup/doors never needed it on touch)
    intent.interact = this.altQueued
    intent.override = this.overrideQueued
    intent.pause = this.pauseQueued
    this.magicQueued = false
    this.altQueued = false
    this.overrideQueued = false
    this.pauseQueued = false
    return this.classic ? this.classicHelper.apply(intent) : intent
  }

  setClassicMode(on: boolean): void {
    this.classic = on
  }

  clearQueued(): void {
    this.magicQueued = false
    this.altQueued = false
    this.overrideQueued = false
    this.pauseQueued = false
  }

  destroy(): void {
    this.layer.innerHTML = ''
  }
}
